/**
 * FinderSidebar Component
 *
 * Category list with selection state.
 */

import { CategoryConfig } from './FinderWindow'
import './finder.css'

interface FinderSidebarProps {
  categories: CategoryConfig[]
  selectedCategory: string
  onCategorySelect: (categoryId: string) => void
  title?: string
}

// Default icons for common category names (by ID)
const defaultIcons: Record<string, string> = {
  career: '💼',
  education: '🎓',
  certifications: '📜',
  skills: '⚡',
  products: '🚀',
  cto: '👑',
  infrastructure: '🖥️',
  developer: '💻',
  product: '💡',
  founder: '🚀',
  locations: '📍',
  experience: '📊',
  companies: '🏢',
  teams: '👥',
}

// Map icon names (e.g., from content.ts) to emojis
const iconNameToEmoji: Record<string, string> = {
  // Material Design / Lucide icon names
  crown: '👑',
  server: '🖥️',
  code: '💻',
  lightbulb: '💡',
  rocket: '🚀',
  briefcase: '💼',
  layers: '📚',
  // Additional common names
  'cto': '👑',
  'infrastructure': '🖥️',
  'developer': '💻',
  'product': '💡',
  'founder': '🚀',
}

export function FinderSidebar({
  categories,
  selectedCategory,
  onCategorySelect,
  title,
}: FinderSidebarProps) {
  const getIcon = (category: CategoryConfig) => {
    if (category.icon) {
      // Check if it's an emoji (starts with emoji character) or needs mapping
      const firstChar = category.icon.codePointAt(0) || 0
      const isEmoji = firstChar > 127 // Basic check for non-ASCII (likely emoji)
      if (isEmoji) return category.icon
      // Map icon name to emoji
      return iconNameToEmoji[category.icon] || defaultIcons[category.id] || '📁'
    }
    return defaultIcons[category.id] || '📁'
  }

  return (
    <div className="finder-sidebar">
      {title && <div className="finder-sidebar__title">{title}</div>}

      <ul className="finder-sidebar__list" role="listbox">
        {categories.map((category) => (
          <li key={category.id}>
            <button
              className={`finder-sidebar__item ${
                selectedCategory === category.id ? 'selected' : ''
              }`}
              onClick={() => onCategorySelect(category.id)}
              role="option"
              aria-selected={selectedCategory === category.id}
            >
              <span className="finder-sidebar__item-icon">
                {getIcon(category)}
              </span>
              <span className="finder-sidebar__item-name">{category.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default FinderSidebar
